import type {
  EcommerceEventMap,
  GTMEventMap,
  LoginMethod,
  ShareContentType,
  ShareMethod,
} from '@utils/gtm/events'

type WindowWithDataLayer = Window & {
  dataLayer: Record<string, any>[]
}

declare const window: WindowWithDataLayer

class GoogleTagManager {
  updateUserProperties(properties: Record<string, any>): void {
    this.pushData({ user_properties: properties })
  }

  trackLogin(method: LoginMethod): void {
    this.trackEvent('login', { login_method: method })
  }

  trackSignUp(method: LoginMethod): void {
    this.trackEvent('sign_up', { sign_up_method: method })
  }

  trackShare(method: ShareMethod, contentType: ShareContentType, itemId: string): void {
    this.trackEvent('share', {
      share_method: method,
      share_content_type: contentType,
      share_item_id: itemId,
    })
  }

  trackEcommerceEvent<T extends keyof EcommerceEventMap>(
    eventType: T,
    data: EcommerceEventMap[T]
  ): void {
    /** @see https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#clear_the_ecommerce_object */
    this.pushData({ ecommerce: null })
    this.pushData({ event: eventType, ...data })
  }

  trackLegacyEvent(category: string, action: string, label?: string, value?: string): void {
    this.pushData({
      event: `legacy_${action}`,
      event_action: action,
      event_category: category,
      event_label: label,
      event_value: value,
    })
  }

  trackEvent<T extends keyof GTMEventMap>(eventType: T, data: GTMEventMap[T]): void {
    this.pushData({ event: eventType, ...data })
  }

  private pushData(data: Record<string, any>): void {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(data)
  }
}

const googleTagManager = new GoogleTagManager()
export default googleTagManager
